import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import { OverlayContainer } from 'react-aria';
import { FocusScope, usePreventScroll, useButton } from 'react-aria';

import { useEventCallback } from '../../../hooks/useEventCallback';
import { useI18n } from '../../../hooks/useI18nFormatters';

import { su } from '../../../theme/functions/su.ts';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { LiveVideo } from './LiveVideo';

const AUTO_HIDE_CONTROLS_DELAY = 5000;
const ANIMATION_DURATION = 300;

export interface SourceRect {
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface CameraFullscreenProps {
  isOpen: boolean;
  onClose?: () => void;
  sourceRect?: SourceRect | null;
  video?: { id: string };
  backgroundImage?: string;
  name?: string;
  cameraName?: string;
  isPlaying?: boolean;
  includeAudio?: boolean;
  onToggleAudio?: () => void;
  hasAudio?: boolean;
  stream?: MediaStream | null;
  isStreamConnected?: boolean;
}

export function CameraFullscreen(props: CameraFullscreenProps) {
  const { onClose, onToggleAudio } = props;
  const { i18n } = useI18n();

  const [showControls, setShowControls] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const audioButtonRef = useRef<HTMLButtonElement>(null);

  usePreventScroll({ isDisabled: !props.isOpen || isExiting });

  // Calculate initial transform from sourceRect
  useLayoutEffect(() => {
    if (props.isOpen && props.sourceRect && containerRef.current) {
      const scaleX = props.sourceRect.width / window.innerWidth;
      const scaleY = props.sourceRect.height / window.innerHeight;
      const scale = Math.max(scaleX, scaleY);
      const translateX = props.sourceRect.left + props.sourceRect.width / 2 - window.innerWidth / 2;
      const translateY =
        props.sourceRect.top + props.sourceRect.height / 2 - window.innerHeight / 2;

      containerRef.current.style.setProperty('--initial-scale', String(scale));
      containerRef.current.style.setProperty('--initial-translate-x', `${translateX}px`);
      containerRef.current.style.setProperty('--initial-translate-y', `${translateY}px`);
    }
  }, [props.isOpen, props.sourceRect]);

  // When using shared stream, derive loading state from isStreamConnected
  const isVideoLoading = props.stream ? !props.isStreamConnected : false;

  const handleClose = useEventCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsExiting(false);
      onClose?.();
    }, ANIMATION_DURATION);
  });

  // Escape key handler
  useEffect(() => {
    if (!props.isOpen || isExiting) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [props.isOpen, isExiting, handleClose]);

  // Auto-hide controls
  const resetHideControlsTimer = useEventCallback(() => {
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    setShowControls(true);
    hideControlsTimerRef.current = setTimeout(() => {
      if ((props.isPlaying ?? true) && !isVideoLoading) {
        setShowControls(false);
      }
    }, AUTO_HIDE_CONTROLS_DELAY);
  });

  useEffect(() => {
    if (props.isOpen && !isExiting) {
      resetHideControlsTimer();
    }
    return () => {
      if (hideControlsTimerRef.current) {
        clearTimeout(hideControlsTimerRef.current);
      }
    };
  }, [props.isOpen, isExiting, resetHideControlsTimer]);

  function handleToggleAudio() {
    onToggleAudio?.();
    resetHideControlsTimer();
  }

  function handleContainerClick() {
    resetHideControlsTimer();
  }

  const closeButton = useButton({ onPress: handleClose }, closeButtonRef as RefObject<Element>);
  const audioButton = useButton(
    { onPress: handleToggleAudio },
    audioButtonRef as RefObject<Element>,
  );

  const isLive = (props.isPlaying ?? true) && !isVideoLoading;

  if (!props.isOpen && !isExiting) return null;

  return (
    <OverlayContainer>
      <FocusScope contain restoreFocus autoFocus>
        <S.FullscreenContainer
          ref={containerRef}
          onClick={handleContainerClick}
          onPointerMove={handleContainerClick}
          data-is-entering={props.isOpen && !isExiting}
          data-is-exiting={isExiting}
        >
          {/* Top Controls Overlay */}
          <S.TopOverlay data-visible={showControls}>
            {/* Top Left: Name, Live indicator */}
            <S.TopLeft>
              <S.NameAndStatus>
                <S.CameraName>
                  {props.name ?? 'Camera'}
                  {props.cameraName && ` • ${props.cameraName}`}
                </S.CameraName>
                <S.LiveStatus>
                  {isLive && (
                    <>
                      <S.LiveDot />
                      <S.LiveText>{i18n.messageFormatter('video.live')}</S.LiveText>
                    </>
                  )}
                  {isVideoLoading && (
                    <S.StatusText>{i18n.messageFormatter('video.loadingStream')}</S.StatusText>
                  )}
                </S.LiveStatus>
              </S.NameAndStatus>
            </S.TopLeft>

            {/* Top Right: Audio control, Close */}
            <S.TopRight>
              {props.hasAudio && (
                <S.AudioButton
                  {...audioButton.buttonProps}
                  ref={audioButtonRef}
                  data-is-pressed={audioButton.isPressed}
                >
                  <S.AudioIcon
                    style={{
                      '--icon-url': `url("${
                        (props.includeAudio ?? false)
                          ? glyph.capability.volumeSet
                          : glyph.capability.volumeMuteCross
                      }")`,
                    }}
                  />
                </S.AudioButton>
              )}
              <S.CloseButton
                {...closeButton.buttonProps}
                ref={closeButtonRef}
                data-is-pressed={closeButton.isPressed}
              >
                <S.CloseIcon
                  style={{
                    '--icon-url': `url("${glyph.core.cross}")`,
                  }}
                />
              </S.CloseButton>
            </S.TopRight>
          </S.TopOverlay>

          {/* Video Area */}
          <S.VideoArea>
            {props.video && (
              <LiveVideo
                key={props.video.id}
                playing={props.isPlaying ?? true}
                video={props.video}
                includeAudio={props.includeAudio ?? false}
                backgroundImage={props.backgroundImage}
                stream={props.stream ?? null}
                isStreamConnected={props.isStreamConnected ?? false}
              />
            )}
          </S.VideoArea>
        </S.FullscreenContainer>
      </FocusScope>
    </OverlayContainer>
  );
}

const expandAnimation = keyframes`
  from {
    transform: translate(var(--initial-translate-x, 0), var(--initial-translate-y, 0))
               scale(var(--initial-scale, 1));
    opacity: 0;
  }
  to {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
`;

const collapseAnimation = keyframes`
  from {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  to {
    transform: translate(var(--initial-translate-x, 0), var(--initial-translate-y, 0))
               scale(var(--initial-scale, 1));
    opacity: 0;
  }
`;

function S() {}
CameraFullscreen.S = S;

S.FullscreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: ${theme.color.black};
  display: flex;
  flex-direction: column;
  z-index: ${theme.zIndex.overlay};
  transform-origin: center center;

  &[data-is-entering='true'] {
    animation: ${expandAnimation} ${ANIMATION_DURATION}ms ease-out forwards;
  }

  &[data-is-exiting='true'] {
    animation: ${collapseAnimation} ${ANIMATION_DURATION}ms ease-in forwards;
  }
`;

S.TopOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${su(2)};
  padding-top: calc(${su(2)} + env(safe-area-inset-top));
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
  z-index: 10;
  opacity: 1;
  transition: opacity 0.2s ease;
  pointer-events: none;

  &[data-visible='false'] {
    opacity: 0;
  }

  > * {
    pointer-events: auto;
  }
`;

S.TopLeft = styled.div`
  display: flex;
  align-items: flex-start;
`;

S.NameAndStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

S.TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(2)};
`;

S.CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${theme.color.black_o_30};
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &[data-is-pressed='true'] {
    transform: scale(0.95);
  }
`;

interface IconCSSProperties extends React.CSSProperties {
  '--icon-url': string;
}

S.CloseIcon = styled.div<{ style: IconCSSProperties }>`
  width: 20px;
  height: 20px;
  background-color: ${theme.color.white};
  mask-image: var(--icon-url);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: var(--icon-url);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

S.CameraName = styled.div`
  font-size: 17px;
  font-weight: 500;
  color: ${theme.color.white};
`;

S.LiveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1)};
  height: 16px;
`;

S.LiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${theme.color.red};
`;

S.LiveText = styled.span`
  color: ${theme.color.white};
  font-size: 14px;
`;

S.StatusText = styled.span`
  color: ${theme.color.white};
  font-size: 14px;
`;

S.AudioButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &[data-is-pressed='true'] {
    transform: scale(0.95);
  }
`;

S.AudioIcon = styled.div<{ style: IconCSSProperties }>`
  width: 32px;
  height: 32px;
  background-color: ${theme.color.white};
  mask-image: var(--icon-url);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: var(--icon-url);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

S.VideoArea = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
